import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyApiService } from '../../api/surveyApi';
import {
    ChevronLeftIcon,
    CalendarIcon,
    GlobeAltIcon,
    ClockIcon,
    ComputerDesktopIcon,
    CheckCircleIcon,
    QueueListIcon,
    StarIcon as StarIconOutline
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const SurveySubmissionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const data = await surveyApiService.getSubmissionDetail(id);
                setSubmission(data);
            } catch (error) {
                console.error('Error fetching submission detail:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!submission) return <div className="p-8 text-center text-rose-500 font-bold">Katılım detayı bulunamadı.</div>;

    const renderAnswer = (response) => {
        const { question_type, numeric_answer, text_answer, selected_options_labels = [] } = response;

        switch (question_type) {
            case 'star_rating':
                return (
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                            star <= numeric_answer
                                ? <StarIconSolid key={star} className="w-6 h-6 text-amber-400" />
                                : <StarIconOutline key={star} className="w-6 h-6 text-slate-200 dark:text-slate-800" />
                        ))}
                    </div>
                );
            case 'boolean':
                const boolText = selected_options_labels?.[0]?.tr || selected_options_labels?.[0] || 'Yanıtlanmadı';
                return (
                    <span className={`px-4 py-2 rounded-xl font-bold text-sm ${boolText === 'Evet' || boolText === 'Yes' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        {boolText}
                    </span>
                );
            case 'checkbox':
                return (
                    <div className="flex flex-wrap gap-2">
                        {selected_options_labels.map((label, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold border border-slate-200 dark:border-slate-700">
                                {label.tr || label}
                            </span>
                        ))}
                    </div>
                );
            case 'radio':
            case 'likert_5':
            case 'range_100':
                return (
                    <div className="flex items-center gap-3">
                        <span className="text-2xl font-black text-indigo-600">
                            {numeric_answer || selected_options_labels?.[0]?.tr || selected_options_labels?.[0] || '-'}
                        </span>
                        {question_type === 'range_100' && <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Puan</span>}
                    </div>
                );
            case 'text_long':
            case 'textarea':
                return (
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl text-slate-700 dark:text-slate-300 font-medium leading-relaxed italic border-l-4 border-indigo-500">
                        "{text_answer || 'Boş bırakıldı'}"
                    </div>
                );
            default:
                return (
                    <span className="text-slate-900 dark:text-white font-bold text-lg">
                        {text_answer || numeric_answer || selected_options_labels?.[0]?.tr || selected_options_labels?.[0] || 'Cevapsız'}
                    </span>
                );
        }
    };

    return (
        <div className="p-8 max-w-4xl mx-auto space-y-8">
            {/* Navigation Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-bold transition-all group"
                >
                    <ChevronLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    Geri Dön
                </button>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Katılım ID:</span>
                    <span className="text-sm font-black text-slate-900 dark:text-white">#{submission.id}</span>
                </div>
            </div>

            {/* Summary Information Card */}
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-100 dark:border-slate-800 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8">
                    {submission.is_completed ? (
                        <div className="flex flex-col items-end gap-1">
                            <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center">
                                <CheckCircleIcon className="w-6 h-6 text-emerald-500" />
                            </div>
                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Tamamlandı</span>
                        </div>
                    ) : (
                        <div className="flex flex-col items-end gap-1 opacity-50">
                            <div className="w-12 h-12 bg-amber-50 dark:bg-amber-500/10 rounded-full flex items-center justify-center">
                                <ClockIcon className="w-6 h-6 text-amber-500" />
                            </div>
                            <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Yarım Kaldı</span>
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="space-y-1">
                        <p className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em]">ANKET SONUCU</p>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                            {submission.survey_title?.tr || Object.values(submission.survey_title || {})[0]}
                        </h1>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                                <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <CalendarIcon className="w-5 h-5" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tarih</span>
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                                        {submission.started_at ? format(new Date(submission.started_at), 'd MMMM yyyy HH:mm', { locale: tr }) : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                                <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <GlobeAltIcon className="w-5 h-5" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">IP Adresi</span>
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                                        {submission.ip_address || 'Bilinmiyor'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                                <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <ComputerDesktopIcon className="w-5 h-5" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Cihaz Bilgisi</span>
                                    <span className="text-[10px] font-medium text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">
                                        {submission.user_agent || 'Bilinmiyor'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                                <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                    <QueueListIcon className="w-5 h-5" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Toplam Yanıt</span>
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                                        {submission.responses.length} Soru
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Responses List */}
            <div className="space-y-6">
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-indigo-500 rounded-full"></span>
                    Soru Bazlı Yanıtlar
                </h2>

                <div className="space-y-4">
                    {submission.responses.map((resp, idx) => (
                        <div key={resp.id} className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm transition-all hover:shadow-md group">
                            <div className="flex flex-col space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em]">SORU {idx + 1}</span>
                                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-[8px] font-black text-slate-400 uppercase rounded">
                                                {resp.question_type}
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-snug">
                                            {resp.question_title?.tr || Object.values(resp.question_title || {})[0]}
                                        </h3>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-50 dark:border-slate-800/50">
                                    <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">KATILIMCI YANITI</div>
                                    {renderAnswer(resp)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SurveySubmissionDetail;
